import React, { ReactElement, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import { requestAuditLogColdExport } from '../../../api/firebase/function/security/auditLogFunction';
import CustomDatePicker from '../../uiParts/form/CustomDatePicker';
import {
  gray5,
  gray40,
  gray80,
  primaryButtonBackgroundColor,
  red50,
} from '../../../constants/colors';
import {
  boldXs,
  dialogTitle as dialogTitleStyle,
  regularSm,
  regularXs,
} from '../../../constants/location';
import {
  AUDIT_HOT_RETENTION_MONTHS,
  AUDIT_TOTAL_RETENTION_MONTHS,
  AUDIT_COLD_EXPORT_URL_TTL_HOURS,
  AUDIT_LOG_EXPORT_MAX,
  auditOperationLabelFor,
} from '../../../constants/enum';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 代理モード / 運営スタッフ時の対象企業。companyAdmin 自身は未指定でよい */
  companyId?: string;
  /** 監査ログタブで入力中の絞り込み条件（そのまま抽出条件として適用する） */
  filters: {
    user_email?: string;
    action?: string;
    resource_type?: string;
    ip_address?: string;
  };
  /** 依頼受付成功時（親でスナックバー表示） */
  onSubmitted: () => void;
}

const styles = {
  description: {
    ...regularSm,
    color: gray80,
    marginBottom: '16px',
    whiteSpace: 'pre-wrap',
  },
  note: {
    ...regularXs,
    color: gray80,
    marginTop: '12px',
    whiteSpace: 'pre-wrap',
  },
  errorText: {
    ...regularSm,
    color: red50,
    marginTop: '12px',
  },
  dateRow: {
    display: 'flex',
    gap: '12px',
  },
  dateItem: {
    flex: 1,
  },
  filterBox: {
    backgroundColor: gray5,
    borderRadius: '4px',
    marginTop: '16px',
    padding: '12px',
  },
  filterTitle: {
    ...boldXs,
    color: gray80,
    marginBottom: '4px',
  },
  filterLine: {
    ...regularXs,
    color: gray80,
    overflowWrap: 'anywhere',
  },
  submitWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  submitButton: {
    height: '44px',
    minWidth: '120px',
    padding: '16px',
  },
};

/**
 * 監査ログ 過去ログ（コールド層）エクスポート依頼ダイアログ（#2985）。
 * ログは BigQuery に保管されており、非同期抽出のうえ
 * ダウンロード URL（24 時間有効）を依頼者のメールアドレスへ送付する。
 * 対象は保存期間（13 ヶ月）内の任意の期間で、直近のログも含まれる。
 * 「直近 3 ヶ月より前だけ」と読める文言で古い期間へ誘導し、
 * データが存在しない期間を指定させてしまっていたため改めた（#3217）。
 */
const AuditLogColdExportDialog = ({
  open,
  onClose,
  companyId,
  filters,
  onSubmitted,
}: Props): ReactElement => {
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState('');

  // 依頼時にそのまま適用される絞り込み条件を実値で見せる。監査ログ画面に
  // 入力が残っていることに気付かないまま 0 件になる事故を防ぐ（#3217）
  const filterLines: string[] = [];
  if (filters.user_email) filterLines.push(`操作者（部分一致）: ${filters.user_email}`);
  if (filters.action) {
    filterLines.push(
      `操作: ${auditOperationLabelFor(filters.action, filters.resource_type ?? '')}`,
    );
  }
  if (filters.ip_address) filterLines.push(`IP アドレス: ${filters.ip_address}`);

  const handleClose = (): void => {
    if (submitting) return;
    setErrorText('');
    onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    if (!from || !to) {
      setErrorText('開始日と終了日を指定してください。');
      return;
    }
    if (from.getTime() > to.getTime()) {
      setErrorText('開始日は終了日以前を指定してください。');
      return;
    }
    setSubmitting(true);
    setErrorText('');
    try {
      await requestAuditLogColdExport({
        company_id: companyId,
        from: from.toISOString(),
        // 終了日は当日の 23:59:59 まで含める
        to: new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999).toISOString(),
        user_email: filters.user_email,
        action: filters.action,
        resource_type: filters.resource_type,
        ip_address: filters.ip_address,
      });
      setFrom(null);
      setTo(null);
      onSubmitted();
      onClose();
    } catch (e) {
      Sentry.captureException(e);
      setErrorText(e instanceof Error ? e.message : 'エクスポートの依頼に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          border: `1px solid ${gray40}`,
          boxSizing: 'border-box',
          margin: '16px',
          padding: '40px',
          '@media screen and (max-width: 960px)': {
            padding: '32px 16px',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          ...dialogTitleStyle,
          padding: 0,
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        過去ログのエクスポート
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: 0, overflow: 'visible' }}>
        <Typography sx={styles.description}>
          {`保存期間（最大 ${AUDIT_TOTAL_RETENTION_MONTHS} ヶ月）内の任意の期間の監査ログを CSV でエクスポートします。\n抽出には時間がかかるため、完了後にダウンロード URL をメールでお送りします。`}
        </Typography>
        <Box sx={styles.dateRow}>
          <Box sx={styles.dateItem}>
            <CustomDatePicker
              labelText="開始日"
              isRequired
              value={from}
              onChange={(value): void => setFrom(value)}
              width="100%"
              height="44px"
              fontSizeIs14px
              formMb="zero"
              hideHelperText
            />
          </Box>
          <Box sx={styles.dateItem}>
            <CustomDatePicker
              labelText="終了日"
              isRequired
              value={to}
              onChange={(value): void => setTo(value)}
              width="100%"
              height="44px"
              fontSizeIs14px
              formMb="zero"
              hideHelperText
            />
          </Box>
        </Box>
        <Box sx={styles.filterBox}>
          <Typography sx={styles.filterTitle}>
            適用される絞り込み条件（監査ログ画面で入力中の検索条件）
          </Typography>
          {filterLines.length > 0 ? (
            filterLines.map(line => (
              <Typography key={line} sx={styles.filterLine}>
                {line}
              </Typography>
            ))
          ) : (
            <Typography sx={styles.filterLine}>なし（期間内のすべてのログを出力します）</Typography>
          )}
        </Box>
        <Typography sx={styles.note}>
          {`・直近 ${AUDIT_HOT_RETENTION_MONTHS} ヶ月のログは、監査ログ画面から直接 CSV 出力することもできます。\n・1 回のエクスポート上限は ${AUDIT_LOG_EXPORT_MAX.toLocaleString()} 件です。超過分は期間を分割して再実行してください。\n・ダウンロード URL の有効期限は ${AUDIT_COLD_EXPORT_URL_TTL_HOURS} 時間です。`}
        </Typography>
        {errorText && <Typography sx={styles.errorText}>{errorText}</Typography>}
        <Box sx={styles.submitWrapper}>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[primaryButtonBackgroundColor, styles.submitButton]}
          >
            エクスポート
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AuditLogColdExportDialog;
